import { LoadingState } from '../ui';
// src/pages/Fixtures.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import SidebarNavbar from '../components/SidebarNavbar';
import BottomNavbar from '../components/BottomNavbar';
import FixtureCardWithAnalytics from '../components/FixtureCardWithAnalytics';
import notify from '../lib/notify';
import { Button as FcButton, Card as FcCard, Input as FcInput, Select as FcSelect } from '../ui';

export default function Fixtures({ session, userRole, username }) {
  const [loading, setLoading] = useState(true);
  const [fixtures, setFixtures] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'matchday'
  const [selectedMatchday, setSelectedMatchday] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('2026/27'); // Default to current season
  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [fixturesByLeague, setFixturesByLeague] = useState({});
  const [fixturesByDate, setFixturesByDate] = useState({});

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  useEffect(() => {
    fetchLeagues();
    fetchSeasons();
    fetchFixtures();
  }, []);

  useEffect(() => {
    fetchFixtures();
  }, [selectedDate, selectedLeague, viewMode, selectedMatchday, selectedSeason]);

  const fetchSeasons = async () => {
    try {
      const { data, error } = await supabase
        .from('fixtures')
        .select('season')
        .order('season', { ascending: false });

      if (error) throw error;

      const uniqueSeasons = [...new Set(data.map(f => f.season))].filter(Boolean);
      setSeasons(uniqueSeasons);
    } catch (error) {
      notify('Failed to fetch seasons', 'error');
    }
  };

  const fetchLeagues = async () => {
    try {
      const { data, error } = await supabase
        .from('fixtures')
        .select('league')
        .order('league');

      if (error) throw error;

      const uniqueLeagues = [...new Set(data.map(f => f.league))].filter(Boolean);
      setLeagues(uniqueLeagues);
    } catch (error) {
      notify('Failed to fetch leagues', 'error');
    }
  };

  const fetchFixtures = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('fixtures')
        .select('*')
        .order('kickoff_time', { ascending: true });

      if (viewMode === 'day') {
        // Filter by date
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        query = query
          .gte('kickoff_time', startOfDay.toISOString())
          .lte('kickoff_time', endOfDay.toISOString());
      } else if (viewMode === 'matchday' && selectedMatchday) {
        // Filter by matchday and season
        query = query
          .eq('match_day', selectedMatchday)
          .eq('season', selectedSeason);
      }

      if (selectedLeague !== 'all') {
        query = query.eq('league', selectedLeague);
      }

      const { data, error } = await query;

      if (error) throw error;

      setFixtures(data || []);
      
      if (viewMode === 'matchday') {
        groupFixturesByDate(data || []);
      } else {
        groupFixturesByLeague(data || []);
      }
      
    } catch (error) {
      notify('Failed to fetch fixtures', 'error');
    } finally {
      setLoading(false);
    }
  };

  const groupFixturesByDate = (fixturesData) => {
    const grouped = fixturesData.reduce((acc, fixture) => {
      const date = fixture.kickoff_time
        ? new Date(fixture.kickoff_time).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })
        : 'TBD';
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(fixture);
      return acc;
    }, {});
    setFixturesByDate(grouped);
  };

  const groupFixturesByLeague = (fixturesData) => {
    const grouped = fixturesData.reduce((acc, fixture) => {
      const league = fixture.league || 'Unknown League';
      if (!acc[league]) {
        acc[league] = [];
      }
      acc[league].push(fixture);
      return acc;
    }, {});
    
    // Sort leagues with Premier League first, then alphabetically
    const sortedGrouped = Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'Premier League') return -1;
        if (b === 'Premier League') return 1;
        return a.localeCompare(b);
      })
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});
    
    setFixturesByLeague(sortedGrouped);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const navigateMatchday = (direction) => {
    const newMatchday = Math.max(1, Math.min(38, (selectedMatchday || 1) + direction));
    setSelectedMatchday(newMatchday);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (kickoffTime) => {
    if (!kickoffTime) return 'TBD';
    return new Date(kickoffTime).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreDisplay = (fixture) => {
    if (fixture.status === 'abandoned') return 'Ab';
    if (fixture.status === 'postponed') return 'Po';
    if (fixture.status === 'complete' && fixture.home_score !== null && fixture.away_score !== null) {
      return `${fixture.home_score} - ${fixture.away_score}`;
    }
    return formatTime(fixture.kickoff_time);
  };

  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Using enhanced FixtureCardWithAnalytics component with analytics cache

  const DateSection = ({ dateName, dateFixtures }) => (
    <div key={dateName} className="mb-6">
      <div className="flex items-center mb-3">
        <div className="h-px bg-gradient-to-r from-[#809076] to-[#F8D794] flex-1 mr-3"></div>
        <h3 className="text-md font-medium text-ui-green px-3 bg-[#F8F9FA] rounded-full border border-ui-border">
          {dateName}
        </h3>
        <div className="h-px bg-gradient-to-l from-[#809076] to-[#F8D794] flex-1 ml-3"></div>
      </div>
      
      <div className="space-y-3">
        {dateFixtures.map((fixture) => (
          <FixtureCardWithAnalytics key={fixture.id} fixture={fixture} />
        ))}
      </div>
    </div>
  );

  const LeagueSection = ({ leagueName, leagueFixtures }) => (
    <div key={leagueName} className="mb-6">
      <div className="flex items-center mb-3">
        <div className="h-px bg-gradient-to-r from-ui-copper to-[#F8D794] flex-1 mr-3"></div>
        <h3 className="text-lg font-semibold text-ui-green px-3 bg-[#F8F9FA] rounded-full border border-ui-border">
          {leagueName}
        </h3>
        <div className="h-px bg-gradient-to-l from-ui-copper to-[#F8D794] flex-1 ml-3"></div>
      </div>
      
      <div className="space-y-3">
        {leagueFixtures.map((fixture) => (
          <FixtureCardWithAnalytics key={fixture.id} fixture={fixture} />
        ))}
      </div>
    </div>
  );

  if (loading && fixtures.length === 0) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
        <SidebarNavbar session={session} userRole={userRole} username={username} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">⚽</div>
            <LoadingState label="Loading fixtures..." />
          </div>
        </div>
        <BottomNavbar />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SidebarNavbar session={session} userRole={userRole} username={username} onLogout={handleLogout} />
      
      <main id="fc-main-content" className="fc-workspace">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-ui-green mb-2">Fixtures</h1>
          <p className="text-content-secondary">
            View match schedules and results
          </p>
        </div>

        {/* Controls */}
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg border border-ui-border p-4 shadow-sm mb-6">
          {/* View Mode Toggle and League Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="bg-gray-100 p-1 rounded-lg flex mx-auto sm:mx-0">
              <FcButton variant="primary" type="submit" aria-pressed={viewMode === 'day'}
                onClick={() => setViewMode('day')}
                className={`${
                  viewMode === 'day'
                    ? ""
                    : ""
                }`}
              >
                Day View
              </FcButton>
              <FcButton aria-pressed={viewMode === 'matchday'} variant="primary" type="submit"
                onClick={() => {
                  setViewMode('matchday');
                  if (!selectedMatchday) setSelectedMatchday(1);
                }}
                className={`${
                  viewMode === 'matchday'
                    ? ""
                    : ""
                }`}
              >
                Matchday View
              </FcButton>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <FcSelect aria-label="All Leagues"
                value={selectedLeague}
                onChange={(e) => setSelectedLeague(e.target.value)}
                className="border focus:border-transparent w-full sm:w-auto"
              >
                <option value="all">All Leagues</option>
                {leagues.map(league => (
                  <option key={league} value={league}>{league}</option>
                ))}
              </FcSelect>

              {viewMode === 'matchday' && (
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <FcSelect aria-label="Season"
                    value={selectedSeason}
                    onChange={(e) => setSelectedSeason(e.target.value)}
                    className="border focus:border-transparent w-full sm:w-auto"
                  >
                    {seasons.map(season => (
                      <option key={season} value={season}>{season}</option>
                    ))}
                  </FcSelect>
                  
                  <FcSelect aria-label="Matchday"
                    value={selectedMatchday}
                    onChange={(e) => setSelectedMatchday(parseInt(e.target.value))}
                    className="border focus:border-transparent w-full sm:w-auto"
                  >
                    {Array.from({ length: 38 }, (_, i) => i + 1).map(md => (
                      <option key={md} value={md}>Matchday {md}</option>
                    ))}
                  </FcSelect>
                </div>
              )}
            </div>
          </div>

          {/* Date Navigation */}
          {viewMode === 'day' && (
            <div className="flex items-center justify-between mb-4">
              <FcButton variant="tertiary" type="submit" aria-label="Previous day"
                onClick={() => navigateDate(-1)}
                className=""
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </FcButton>
              
              <div className="text-center flex-1 mx-4">
                <div className="relative inline-block">
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => document.getElementById('date-picker').showPicker()}
                    className="cursor-pointer mb-2 block"
                  >
                    {formatDate(selectedDate)}
                  </FcButton>
                  <FcInput
                    id="date-picker" aria-label="Fixture date"
                    type="date"
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                    className="sr-only"
                  />
                </div>
                {!isToday() && (
                  <FcButton variant="tertiary" type="submit"
                    onClick={goToToday}
                    className="hover:underline"
                  >
                    Go to Today
                  </FcButton>
                )}
              </div>
              
              <FcButton variant="tertiary" type="submit" aria-label="Next day"
                onClick={() => navigateDate(1)}
                className=""
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </FcButton>
            </div>
          )}

          {/* Matchday Navigation */}
          {viewMode === 'matchday' && (
            <div className="flex items-center justify-between mb-4">
              <FcButton variant="tertiary" type="submit" aria-label="Previous matchday"
                onClick={() => navigateMatchday(-1)}
                disabled={selectedMatchday <= 1}
                className="disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </FcButton>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-ui-green">
                  Matchday {selectedMatchday}
                </div>
                <div className="text-sm text-content-secondary">
                  {selectedSeason}
                </div>
              </div>
              
              <FcButton variant="tertiary" type="submit" aria-label="Next matchday"
                onClick={() => navigateMatchday(1)}
                disabled={selectedMatchday >= 38}
                className="disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </FcButton>
            </div>
          )}
        </FcCard>

        {/* Fixtures Content */}
        {loading ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">⚽</div>
            <LoadingState label="Loading fixtures..." />
          </div>
        ) : fixtures.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📅</div>
            <div className="text-xl text-content-secondary mb-2">No fixtures found</div>
            <div className="text-content-secondary">
              {viewMode === 'day' 
                ? `No matches scheduled for ${formatDate(selectedDate)}`
                : `No matches found for Matchday ${selectedMatchday}`
              }
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {viewMode === 'matchday' ? (
              // Matchday view - always group by date
              Object.entries(fixturesByDate).map(([dateName, dateFixtures]) => (
                <DateSection key={dateName} dateName={dateName} dateFixtures={dateFixtures} />
              ))
            ) : (
              // Day view - group by league
              selectedLeague === 'all' ? (
                Object.entries(fixturesByLeague).map(([leagueName, leagueFixtures]) => (
                  <LeagueSection key={leagueName} leagueName={leagueName} leagueFixtures={leagueFixtures} />
                ))
              ) : (
                <div className="space-y-3">
                  {fixtures.map((fixture) => (
                    <FixtureCardWithAnalytics key={fixture.id} fixture={fixture} />
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </main>

      <BottomNavbar />
    </div>
  );
}